
import React, { useState, useEffect } from 'react';
import { 
    Shield, Database, BrainCircuit, Globe, 
    Trash2, Save, Power, Settings as SettingsIcon,
    RefreshCw, ShieldCheck, Cpu, HardDrive
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { at } from '../../../i18nAdmin';
import { clearCache } from '../../../services/storageService';
import { useToast } from '../../../context/ToastContext';
import { FullPageLoader } from '../../Common/FullPageLoader';

export const SettingsTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [isClearingCache, setIsClearingCache] = useState(false);
    
    // System States
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [guestPosting, setGuestPosting] = useState(true);
    const [aiAnalysisModel, setAiAnalysisModel] = useState('gemini-flash-lite-latest');

    const handleSaveSettings = () => {
        setIsSaving(true);
        // 향후 Supabase의 별도 config 테이블에 저장하는 로직으로 확장 가능
        setTimeout(() => {
            setIsSaving(false);
            showToast('success', at(lang, 'configSaved'));
        }, 800);
    };

    const handleClearSystemCache = () => {
        if (window.confirm(at(lang, 'flushConfirm'))) {
            setIsClearingCache(true);
            clearCache();
            setTimeout(() => {
                setIsClearingCache(false);
                window.location.reload();
            }, 2000);
        }
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8 pb-20">
            {isClearingCache && (
                <div className="fixed inset-0 z-[100]">
                    <FullPageLoader 
                        isLoading={true} 
                        icon={RefreshCw} 
                        text={at(lang, 'clearingCache')} 
                    />
                </div>
            )}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">{at(lang, 'settings')}</h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                        {at(lang, 'manageSettingsDesc')}
                    </p>
                </div>
                <button 
                    onClick={handleSaveSettings}
                    disabled={isSaving}
                    className="flex items-center gap-3 bg-slate-900 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-100 md:hover:bg-rose-500 transition-all active:scale-95 disabled:opacity-50"
                >
                    {isSaving ? <RefreshCw size={18} className="animate-spin" /> : <><Save size={18} /> {at(lang, 'save')}</>}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Global Protocols & AI */}
                <div className="space-y-6">
                    <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white">
                                <Shield size={20} />
                            </div>
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{at(lang, 'systemProtocols')}</h3>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-100 transition-all">
                                <div className="flex items-center gap-4">
                                    <div className={`p-2.5 rounded-xl ${maintenanceMode ? 'bg-amber-500 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>
                                        <Power size={18} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-slate-900 uppercase leading-none mb-1">{at(lang, 'maintenanceMode')}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{at(lang, 'maintenanceDesc')}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setMaintenanceMode(!maintenanceMode)}
                                    className={`w-12 h-6 rounded-full p-1 transition-all ${maintenanceMode ? 'bg-amber-500' : 'bg-slate-200'}`}
                                >
                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${maintenanceMode ? 'translate-x-6' : 'translate-x-0'}`} />
                                </button>
                            </div>

                            <div className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-100">
                                <div className="flex items-center gap-4">
                                    <div className={`p-2.5 rounded-xl ${guestPosting ? 'bg-indigo-500 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>
                                        <Globe size={18} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-slate-900 uppercase leading-none mb-1">{at(lang, 'guestIntelligence')}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{at(lang, 'guestIntelDesc')}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setGuestPosting(!guestPosting)}
                                    className={`w-12 h-6 rounded-full p-1 transition-all ${guestPosting ? 'bg-indigo-500' : 'bg-slate-200'}`}
                                >
                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${guestPosting ? 'translate-x-6' : 'translate-x-0'}`} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm p-8">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center text-white">
                                <BrainCircuit size={20} />
                            </div>
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{at(lang, 'aiNeuralEngine')}</h3>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{at(lang, 'primaryModel')}</label>
                            <select 
                                value={aiAnalysisModel}
                                onChange={(e) => setAiAnalysisModel(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-5 py-4 text-sm font-bold text-slate-900 outline-none focus:border-rose-500 transition-colors"
                            >
                                <option value="gemini-3-flash-preview">Gemini 3 Flash (Real-time)</option>
                                <option value="gemini-3-pro-preview">Gemini 3 Pro (Deep Reasoning)</option>
                                <option value="gemini-2.5-flash-image">Gemini 2.5 Image Forge</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Maintenance & Maintenance */}
                <div className="space-y-6">
                    <div className="bg-slate-900 rounded-[32px] border border-white/5 shadow-2xl p-8 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-10 opacity-5 rotate-12 pointer-events-none">
                            <Database size={100} />
                        </div>
                        
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-emerald-400">
                                    <HardDrive size={20} />
                                </div>
                                <h3 className="text-lg font-black text-white uppercase tracking-tight">{at(lang, 'sysMaintenance')}</h3>
                            </div>

                            <div className="space-y-4">
                                <div className="p-6 bg-white/5 rounded-3xl border border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
                                    <div>
                                        <p className="text-sm font-black text-white uppercase leading-none mb-1">{at(lang, 'flushCache')}</p>
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">{at(lang, 'flushCacheDesc')}</p>
                                    </div>
                                    <button 
                                        onClick={handleClearSystemCache}
                                        disabled={isClearingCache}
                                        className="w-full sm:w-auto px-6 py-3 bg-rose-500/10 md:hover:bg-rose-500 text-rose-500 md:hover:text-white border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {isClearingCache ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />} 
                                        {isClearingCache ? at(lang, 'clearing') : at(lang, 'flushCache')}
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                                        <span className="block text-[9px] font-bold text-slate-500 uppercase mb-1">{at(lang, 'dbStatus')}</span>
                                        <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                            <span className="text-xs font-black text-emerald-500 uppercase">{at(lang, 'operational')}</span>
                                        </div>
                                    </div>
                                    <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                                        <span className="block text-[9px] font-bold text-slate-500 uppercase mb-1">{at(lang, 'apiNode')}</span>
                                        <span className="text-xs font-black text-white uppercase">Primary (V3)</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-[32px] border border-slate-200 p-8 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <ShieldCheck size={18} className="text-emerald-500" />
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{at(lang, 'versionControl')}</span>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-bold text-slate-500 uppercase tracking-tighter">{at(lang, 'coreVersion')}</span>
                                <span className="font-black text-slate-900 uppercase">XL-OS 1.6.0-BETA</span>
                            </div>
                            <div className="h-px bg-slate-50" />
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-bold text-slate-500 uppercase tracking-tighter">{at(lang, 'neuralInterface')}</span>
                                <span className="font-black text-slate-900 uppercase">Gemini 2.5 Integrated</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
